import type {
  Bericht,
  Freigabebefund,
  Freigabeurteil,
  HistorischeKursluecke,
  Messwerte,
  Nachweispruefung,
  Rechenantwort,
} from './typen'
import type { Zustandsart } from './Zustand'
import { Zustandssymbol } from './Zustand'
import { betrag, periodenname } from './api'
import { abschlussstatus, anteil, kontobezeichnung, satz, t, anzahlwort } from './sprache'
import type { Sprachschluessel } from './sprache'
import { bereichsname, findeBereich } from './bereiche'
import { verlangtSatz } from './Pruefregeln'
import { befundtexte, zumRechnen } from './Meldungen'
import type { Meldungskontext, Sprungziel } from './Meldungen'
import { Verweissatz } from './Verweissatz'

/**
 * Die Arbeitsliste — die Startseite (R140).
 *
 * Sie wird **eingesammelt, nicht erfunden**: Jede Zeile hier steht schon
 * irgendwo im Modell. Es wird nichts geschätzt und nichts nachgerechnet, die
 * Freigabeprüfung, die Veraltungsprüfung und die Nachweisprüfung liefern die
 * Zahlen bereits.
 *
 * Drei Eigenschaften machen sie zur Arbeitsliste statt zur Übersicht: Jede
 * Zeile ist eine **Zahl**, jede Zeile ist **anklickbar** und führt dorthin, wo
 * man sie erledigt, und sortiert wird **nach dem, was blockiert** — nicht
 * alphabetisch und nicht nach Bereich.
 */

export type Arbeitszeile = {
  id: string
  zustand: Zustandsart
  /**
   * **Der Schweregrad, links in der Zeile** (7.1 Abschnitt 13, R1531) — die
   * Wörter von „Prüfregeln festlegen". Bis zum 2026-10-10 stand dort die
   * Menge, mal „28 Pflichtangaben", mal „1 harter Befund"; eine Anzahl, die
   * die Arbeit misst, steht seither in der Überschrift.
   */
  schwere: string
  /** Was ist, mit dem Namen der Sache (R1527). */
  was: string
  /**
   * Was zu tun ist und was danach geschieht, das Ziel als Verweis `[[Wort]]`
   * (R1527). Ohne diesen Satz ist die Zeile eine Meldung.
   */
  weg: string | null
  /** Wohin der Verweis führt, mit der Auswahl der Zielseite (R1528). */
  ziel: Sprungziel | null
  /**
   * Ein Knopf, der die Aufgabe an Ort und Stelle erledigt — der eine Klick
   * der historischen Kurse (R1473). Die Zeile selbst führt weiter zu `ziel`.
   */
  aktion?: { text: string; ausfuehren: () => void }
}

const rang: Record<Zustandsart, number> = {
  blockiert: 0,
  veraltet: 1,
  offen: 2,
  fehlt: 3,
  fertig: 4,
}

/*
  **Wohin ein Befund führt, steht in `Meldungen.ts`** (7.1 Abschnitt 13,
  R1528) — an einer Stelle mit dem Konzernabschluss-Monitor, der dieselben Sätze zeigt. Bis
  zum 2026-10-10 stand hier eine eigene Tabelle, und was sie nicht kannte,
  führte ins Cockpit (heute Konzernabschluss-Monitor).
*/

/**
 * Ein Kurs, der nicht echt ist, ist **je Währung, Kursart und Monat** ein
 * eigener Punkt (2.6 Abschnitt 2.10, R1382) — mit dem Satz, der sagt, was
 * angenommen ist und woher es stammt. Kennt das Urteil die Kurse nicht, bleibt
 * es bei der einen Zeile.
 */
function kurszeilen(
  urteil: Freigabeurteil,
  b: Freigabebefund,
  zustand: Zustandsart,
  schwere: string,
  kontext: Meldungskontext,
): Arbeitszeile[] | null {
  const kurse = urteil.kurse ?? []
  if (b.code !== 'kurse-aktualisieren' || kurse.length === 0) {
    return null
  }

  const { weg, ziel } = befundtexte(b, kontext)
  return kurse.map((k, i) => ({
    id: `kurs-${i}`,
    zustand,
    schwere,
    was: satz(k),
    weg,
    ziel,
  }))
}

/**
 * Eine Beteiligung ohne gehaltene Gesellschaft ist **je Betrag** ein eigener
 * Punkt (5.1 Abschnitt 3.38, R930) — mit Gesellschaft, Periode und Betrag.
 *
 * Die Ausnahme von „eine Zeile mit der Zahl": Hier ist nicht eine Abstimmung
 * offen, sondern die Kapitalkonsolidierung unterbleibt, und der Befund vom
 * 2026-09-26 — 1.600.000 still unkonsolidiert — darf nicht in einer Zahl
 * verschwinden. Kennt das Urteil die Beträge nicht, bleibt es bei der einen
 * Zeile.
 */
/**
 * Eine konzerninterne Differenz über der Toleranz ist **je Paar und
 * Gegenstand** ein eigener Punkt (2.9 Abschnitt 13.7, R1400) — die beiden
 * Gesellschaften, was gegenübersteht, und der Betrag. Kennt das Urteil die
 * Paare nicht, bleibt es bei der einen Zeile.
 */
function icdifferenzzeilen(
  urteil: Freigabeurteil,
  b: Freigabebefund,
  zustand: Zustandsart,
  schwere: string,
  kontext: Meldungskontext,
): Arbeitszeile[] | null {
  const paare = urteil.icDifferenzen ?? []
  if (b.code !== 'ic-differenz-nicht-geklaert' || paare.length === 0) {
    return null
  }

  const { weg, ziel } = befundtexte(b, kontext)
  return paare.map((d, i) => ({
    id: `ic-differenz-${i}`,
    zustand,
    schwere,
    // **Bei der Ausschüttung stehen beide Seiten da** (2.9 Abschnitt 13.13,
    // R1510): Ertrag der Mutter, Ausschüttung der Tochter und Quote — wer
    // nur den Unterschied sieht, weiß nicht, welche Seite fehlt.
    was:
      d.gegenstand === 'dividendenkonsolidierung' && d.ertrag != null
        ? t('arbeitsliste.ic-differenz.dividende', {
            gesellschaft: d.gesellschaft,
            partner: d.partner,
            periode: periodenname(urteil.periode),
            ertrag: betrag(d.ertrag),
            ausschuettung: betrag(d.ausschuettung ?? 0),
            quote: anteil(d.quote),
            differenz: betrag(d.differenz),
            waehrung: d.waehrung,
          })
        : t('arbeitsliste.ic-differenz', {
            gesellschaft: d.gesellschaft,
            partner: d.partner,
            gegenstand: t(`ic-differenz.gegenstand.${d.gegenstand}` as Sprachschluessel),
            // Der Betrag bleibt in der Überschrift (R1400): Er stand bis zum
            // 2026-10-10 links in der Zeile, wo jetzt der Schweregrad steht.
            differenz: betrag(d.differenz),
            waehrung: d.waehrung,
          }),
    weg,
    ziel,
  }))
}

function beteiligungszeilen(
  urteil: Freigabeurteil,
  b: Freigabebefund,
  zustand: Zustandsart,
  schwere: string,
  kontext: Meldungskontext,
): Arbeitszeile[] | null {
  const beteiligungen = (urteil.ohnePartner ?? []).filter((p) => p.istBeteiligung)
  if (b.code !== 'beteiligung-ohne-partner' || beteiligungen.length === 0) {
    return null
  }

  const { weg, ziel } = befundtexte(b, kontext)
  return beteiligungen.map((p, i) => ({
    id: `beteiligung-ohne-partner-${i}`,
    zustand,
    schwere,
    // Gesellschaft, Periode und Betrag bleiben in der Überschrift (R930).
    was: t('arbeitsliste.beteiligung-ohne-partner', {
      konto: kontobezeichnung(p.konto),
      einheit: p.einheit,
      periode: periodenname(urteil.periode),
      betrag: betrag(p.betrag),
    }),
    weg,
    ziel,
  }))
}

/**
 * **Der Schweregrad eines Befunds** (R1531) — die Wörter von „Prüfregeln
 * festlegen": Was sperrt, „Blockiert die Freigabe"; ein heruntergestufter
 * Widerspruch „Warnung", wenn er bei der Freigabe einen Satz verlangt, sonst
 * „Hinweis" (R910, R1377).
 */
function schwereDesBefunds(uebergehbar?: 'warnung' | 'hinweis'): string {
  return t(uebergehbar ? `pruefregeln.stufe.${uebergehbar}` : 'pruefregeln.stufe.sperrt')
}

/** Der Schweregrad einer Zeile, die keine Prüfung ist: ihr Zustand (R1531). */
function schwereDesZustands(zustand: Zustandsart): string {
  return t(`arbeitsliste.schwere.${zustand}` as Sprachschluessel)
}

/**
 * Sammelt die Zeilen ein.
 *
 * Bewusst als eigene Funktion und nicht im Bauteil: Die Seitenleiste braucht
 * dieselbe Zahl für den Anhänger am Bereichssymbol, und zwei Stellen, die
 * dasselbe zählen, gehen irgendwann auseinander.
 */
/**
 * **Historische Kurse anlegen** (2.6 Abschnitt 2.12, R1473): je Gesellschaft
 * eine Zeile — der Lauf hält an, bis ihre Tabelle zur Lieferung passt (R1472).
 * Hat jede fehlende Zeile einen Vorschlag, legt ein Klick sie alle an; sonst
 * führt die Zeile auf die Seite, wo der Kurs eingetragen wird — etwa der
 * Mischkurs der Startzeile (R1475).
 */
export function historischeKurszeilen(
  luecken: readonly HistorischeKursluecke[] | null,
  anlegen: (() => void) | null,
): Arbeitszeile[] {
  const jeGesellschaft = new Map<string, HistorischeKursluecke[]>()
  for (const l of luecken ?? []) {
    jeGesellschaft.set(l.einheit, [...(jeGesellschaft.get(l.einheit) ?? []), l])
  }

  return [...jeGesellschaft.values()].map((gruppe) => {
    const erste = gruppe[0] as HistorischeKursluecke
    const mitKlick = gruppe.every((l) => l.fehlt && (l.vorschlag !== null || l.vorschlagsart === 'eintrittskurs'))
    return {
      id: `historisch-${erste.einheit}`,
      zustand: 'blockiert' as const,
      schwere: schwereDesZustands('blockiert'),
      // Die Anzahl misst die Arbeit und steht deshalb in der Überschrift (R1527).
      was: t('arbeitsliste.historisch.was', {
        bewegungen: t(gruppe.length === 1 ? 'arbeitsliste.historisch.menge.eins' : 'arbeitsliste.historisch.menge', {
          anzahl: anzahlwort(gruppe.length),
        }),
        gesellschaft: erste.gesellschaft,
        periode: periodenname(erste.periode),
      }),
      weg: t(mitKlick ? 'arbeitsliste.historisch.weg.klick' : 'arbeitsliste.historisch.weg.eintragen', undefined, true),
      ziel: { bereich: 'stammdaten', unterpunkt: 'historische-kurse' },
      aktion: mitKlick && anlegen ? { text: t('arbeitsliste.historisch.anlegen'), ausfuehren: anlegen } : undefined,
    }
  })
}

export function arbeitszeilen(
  lauf: Rechenantwort | null,
  urteil: Freigabeurteil | null,
  bilanz: Bericht | null,
  pruefung: Nachweispruefung | null,
  messung: Messwerte | null,
  periode: number,
  historisch: Arbeitszeile[] = [],
  /** Was die Meldungen über ihre Lage wissen: der Konzernabschluss (R1527, R1528). */
  kontext: Meldungskontext = {},
): Arbeitszeile[] {
  // Vor allem anderen und auch ohne Lauf: Fehlt eine Zeile der historischen
  // Kurse, hält der Lauf an, und es gibt keinen (R1472).
  const zeilen: Arbeitszeile[] = [...historisch]
  const abschluss = kontext.abschluss?.name ?? ''

  if (!lauf) {
    zeilen.push({
      id: 'keinedaten',
      zustand: 'fehlt',
      schwere: schwereDesZustands('fehlt'),
      was: periode
        ? t('arbeitsliste.keinedaten', { periode: periodenname(periode) })
        : t('arbeitsliste.keinedaten.ohne'),
      // Ohne Daten kein Lauf: Sobald geliefert ist, rechnet die Laufvorsorge (R294).
      weg: t('arbeitsliste.weg.keinedaten', undefined, true),
      ziel: { bereich: 'datenimport', unterpunkt: 'lieferungen' },
    })
    return zeilen
  }

  if (urteil) {
    /**
     * Ein Befund als Zeile — oder, wo das Urteil die Einzelfälle kennt, je
     * Fall eine (R930, R1382, R1400).
     *
     * Der Rechenkern schickt Schlüssel und Werte; der Satz entsteht hier.
     * Deshalb wechselt auch ein Befund die Sprache mit, ohne dass der
     * Abschluss neu gerechnet werden müsste.
     */
    const befundzeilen = (b: Freigabebefund, zustand: Zustandsart, schwere: string, id: string): Arbeitszeile[] => {
      const einzeln =
        beteiligungszeilen(urteil, b, zustand, schwere, kontext) ??
        kurszeilen(urteil, b, zustand, schwere, kontext) ??
        icdifferenzzeilen(urteil, b, zustand, schwere, kontext)
      if (einzeln) {
        return einzeln
      }

      const { was, weg, ziel } = befundtexte(b, kontext)
      return [{ id, zustand, schwere, was, weg, ziel }]
    }

    // Harte Befunde zuerst — sie sind nie übergehbar (R115). Nur wenn der
    // Konzern die Prüfung auf „Sperrt" gestellt hat (R910).
    for (const b of urteil.befunde) {
      zeilen.push(...befundzeilen(b, 'blockiert', schwereDesBefunds(), `befund-${b.code}`))
    }

    // Jede Prüfung auf der Stufe „Warnung“ oder „Hinweis“ (R1377) — die
    // weichen Anhangsbefunde (R836) wie jeder heruntergestufte Widerspruch:
    // Sie sperren nicht, sie stehen als „offen“ da; eine Warnung wird bei der
    // Freigabe begründet (R912), ein Hinweis kommentarlos übergangen.
    for (const h of urteil.hinweise ?? []) {
      const stufe = verlangtSatz(urteil, h.code) ? 'warnung' : 'hinweis'
      zeilen.push(...befundzeilen(h, 'offen', schwereDesBefunds(stufe), `hinweis-${h.code}`))
    }

    // Veraltung, getrennt nach Auslöser (R99) — der Anwender muss wissen,
    // welcher der drei Gründe es war, sonst kann er nichts damit anfangen.
    //
    // **Der Datenstand hat hier keine eigene Zeile mehr** (R294). Er hatte
    // eine, und sie verglich den Stand des Laufs mit dem heutigen — das ist
    // aber nicht die Veraltungsprüfung aus R99, sondern eine gröbere: Die
    // Erfassungsnummer wächst bei jedem Schreibvorgang irgendwo im System,
    // auch bei einem Lauf, der gar nichts mit dieser Periode zu tun hat. Die
    // richtige Prüfung — die höchste Erfassung im Umfang dieses Abschlusses —
    // macht der Freigabedienst und meldet sie als harten Befund; der steht
    // oben in der Liste. Und ist der Abschluss offen, rechnet die Laufvorsorge
    // ihn ohnehin nach, bevor jemand einen Bericht sieht.
    if (urteil.regelwerksausgabe !== urteil.aktuelleAusgabe) {
      zeilen.push({
        id: 'veraltet-regelwerk',
        zustand: 'veraltet',
        schwere: schwereDesZustands('veraltet'),
        was: t('arbeitsliste.veraltet.regelwerk', { abschluss }),
        weg: t('arbeitsliste.weg.neurechnen', undefined, true),
        ziel: zumRechnen(kontext),
      })
    }

    /*
      **Freigabefähig steht als erledigt da** (7.1 Abschnitt 11, R1229) — mit
      grünem Haken. Bis zum 2026-10-06 stand hier „0 Befunde" mit dem Symbol
      eines offenen Punkts. Seit dem 2026-10-10 steht links „Erledigt" und die
      Periode in der Überschrift (R1531).
    */
    if (urteil.freigabefaehig && (urteil.abschlussstatus === 'offen' || urteil.abschlussstatus === 'in_arbeit')) {
      zeilen.push({
        id: 'freigabe',
        zustand: 'fertig',
        schwere: schwereDesZustands('fertig'),
        was: t('arbeitsliste.freigabe', { abschluss, periode: periodenname(urteil.periode) }),
        weg: t('arbeitsliste.weg.freigeben', undefined, true),
        ziel: { bereich: 'abschluss', unterpunkt: 'monitor' },
      })
    }

    if (urteil.abschlussstatus !== 'offen' && urteil.abschlussstatus !== 'in_arbeit') {
      zeilen.push({
        id: 'status',
        zustand: 'fertig',
        schwere: schwereDesZustands('fertig'),
        was: t('arbeitsliste.status', {
          abschluss,
          periode: periodenname(urteil.periode),
          status: abschlussstatus(urteil.abschlussstatus),
        }),
        weg: t('arbeitsliste.weg.status', undefined, true),
        ziel: { bereich: 'nachweis', unterpunkt: 'aenderungsnachweis' },
      })
    }
  }

  // Den Betrag zeigt die Bilanz selbst — hier steht, dass sie nicht aufgeht (R1527).
  if (bilanz && !bilanz.gehtAuf) {
    zeilen.push({
      id: 'bilanzdifferenz',
      zustand: 'blockiert',
      schwere: schwereDesZustands('blockiert'),
      was: t('arbeitsliste.bilanz'),
      weg: t('arbeitsliste.weg.bilanz', undefined, true),
      ziel: { bereich: 'berichte', unterpunkt: 'bilanz' },
    })
  }

  for (const [i, h] of lauf.hinweise.entries()) {
    zeilen.push({
      id: `hinweis-${i}`,
      zustand: 'offen',
      schwere: schwereDesZustands('offen'),
      was: satz(h),
      weg: t('arbeitsliste.weg.schritte', undefined, true),
      ziel: { bereich: 'abschluss', unterpunkt: 'konzernabschluesse' },
    })
  }

  // Hier ohne Ansehen der Rechte: Die Arbeitszeilen fragen nach allem, was
  // gemeldet wird; wer was sieht, entscheidet die Anwendung (R1575).
  zeilen.push(...anwendungszeilen(pruefung, messung, true))

  return zeilen.sort((a, b) => rang[a.zustand] - rang[b.zustand])
}

/**
 * **Was die ganze Anwendung betrifft** — die gebrochene Nachweiskette, eine
 * verletzte Antwortzeitzusage. Es steht unter dem Übersichtsblatt (7.1
 * Abschnitt 12, R1519) und hängt an keiner Auswahl.
 *
 * **Nur bei einer Störung und nur für den, der verwaltet** (R1575,
 * Entscheidung vom 2026-10-11). Bis dahin stand bei jedem Anwender „Alle
 * 1.823 Einträge sind unversehrt“ — eine Zeile ohne Handlungsbedarf, die mehr
 * Fragen aufwarf, als sie beantwortete. Unversehrt steht die Kette im
 * Änderungsnachweis und im Regelnachweis des Prüfers.
 */
export function anwendungszeilen(
  pruefung: Nachweispruefung | null,
  messung: Messwerte | null,
  darfVerwalten: boolean,
): Arbeitszeile[] {
  const zeilen: Arbeitszeile[] = []
  if (!darfVerwalten) {
    return zeilen
  }

  if (pruefung && !pruefung.unversehrt) {
    zeilen.push({
      id: 'nachweis',
      zustand: 'blockiert',
      schwere: schwereDesZustands('blockiert'),
      was: satz(pruefung.meldung),
      weg: t('arbeitsliste.weg.kette', undefined, true),
      ziel: { bereich: 'nachweis', unterpunkt: 'aenderungsnachweis' },
    })
  }

  /*
    **Die Antwortzeit steht hier nur, wenn die Zusage verletzt ist** (7.2
    Abschnitt 37, R1146 — Entscheidung vom 2026-10-06). Bis dahin stand auf
    der Startseite jedes Anwenders „Die Antwortzeitzusage von 2000 ms wird
    über 1.342 Aufrufe gehalten." Gehalten steht sie im Systemzustand.
  */
  if (messung && messung.p95Ms > messung.budgetMs) {
    zeilen.push({
      id: 'antwortzeit',
      zustand: 'offen',
      schwere: t('pruefregeln.stufe.warnung'),
      was: t('arbeitsliste.antwortzeit.verletzt', {
        ueber: anzahlwort(messung.ueberBudget),
        anzahl: anzahlwort(messung.anzahl),
        budget: anzahlwort(messung.budgetMs),
      }),
      weg: t('arbeitsliste.weg.antwortzeiten', undefined, true),
      ziel: { bereich: 'betrieb', unterpunkt: 'antwortzeiten' },
    })
  }

  return zeilen
}

/** Wie viele Zeilen etwas verlangen — der Anhänger am Bereichssymbol (R142). */
export function offenePunkte(zeilen: Arbeitszeile[]): number {
  return zeilen.filter((z) => z.zustand !== 'fertig').length
}

export function Arbeitsliste({
  zeilen,
  kreisname,
  periode,
  aufSprung,
}: {
  zeilen: Arbeitszeile[]
  kreisname: string
  periode: number
  aufSprung: (bereich: string, unterpunkt?: string, auswahl?: Record<string, string>) => void
}) {
  const zuTun = offenePunkte(zeilen)

  return (
    <section className="arbeitsliste">
      <h1>{bereichsname(findeBereich('arbeitsliste'))}</h1>
      <p className="erlaeuterung">
        {kreisname}
        {periode ? ` · ${periodenname(periode)}` : ''} —{' '}
        {zuTun === 0
          ? t('arbeitsliste.kopf.leer')
          : t(zuTun === 1 ? 'arbeitsliste.kopf.eins' : 'arbeitsliste.kopf.mehrere', {
              anzahl: anzahlwort(zuTun),
            })}
      </p>

      <Vorrat zeilen={zeilen} aufSprung={aufSprung} />
    </section>
  )
}

/** Die Zeilen der Arbeitsliste, je mit Zustand, Schweregrad und dem Satz mit Verweis (R1527–R1531). */
export function Vorrat({
  zeilen,
  aufSprung,
}: {
  zeilen: Arbeitszeile[]
  aufSprung: (bereich: string, unterpunkt?: string, auswahl?: Record<string, string>) => void
}) {
  return (
    <>
      {/*
        **Die Zeile ist kein Knopf** (R1531): Geklickt wird der Verweis im
        Satz darunter. Eine ganze Zeile als Knopf mit einem zweiten Verweis
        darin ließe offen, wohin ein Klick führt.
      */}
      <ul className="vorrat">
        {zeilen.map((z) => {
          const ziel = z.ziel
          return (
            <li key={z.id} className={`arbeitszeile ${z.zustand}`}>
              <Zustandssymbol art={z.zustand} />
              <span className="schwere">{z.schwere}</span>
              <span className="text">
                {z.was}
                {z.weg && (
                  <span className="weg">
                    <Verweissatz
                      text={z.weg}
                      aufVerweis={ziel ? () => aufSprung(ziel.bereich, ziel.unterpunkt, ziel.auswahl) : null}
                    />
                  </span>
                )}
              </span>
              {z.aktion && (
                <button type="button" className="schmal" onClick={z.aktion.ausfuehren}>
                  {z.aktion.text}
                </button>
              )}
            </li>
          )
        })}
      </ul>
    </>
  )
}
